import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, Video, Volume2, VolumeX } from "lucide-react";
import M from "@/components/demo/manifest.json";
import { FirmCtx, H, W, rise } from "@/components/demo/DemoKit";
import { SceneIntro, SceneOutreach, SceneRadar, SceneWhy, SceneWorkflow } from "@/components/demo/ScenesRCC";
import { SceneBestFit, SceneCTA, SceneRevenue, SceneSourcing } from "@/components/demo/ScenesSI";
import { SceneInbox, SceneSequence, SceneWarm } from "@/components/demo/ScenesFlow";
import { ScenePipeline, SceneReverse } from "@/components/demo/ScenesWin";
import { trackDemo } from "@/lib/demoTrack";
import { cn } from "@/lib/utils";

export const SCENES = { intro: [SceneIntro, "Today's focus"], workflow: [SceneWorkflow, "Workflow"], radar: [SceneRadar, "Radar"], why: [SceneWhy, "Why now"], warm: [SceneWarm, "Warm paths"], outreach: [SceneOutreach, "Outreach"], sequence: [SceneSequence, "Sequences"], inbox: [SceneInbox, "Replies"], sourcing: [SceneSourcing, "Sourcing"], bestfit: [SceneBestFit, "Best fit"], reverse: [SceneReverse, "Reverse match"], pipeline: [ScenePipeline, "Pipeline"], revenue: [SceneRevenue, "Revenue"], cta: [SceneCTA, "Book a demo"] };
export const DEMO_VIDEO = "/demo/rpopro-product-demo.mp4";
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Sentence shown at local time lt when `text` is spoken over `speak` seconds.
export const captionAt = (text, lt, speak) => {
  const parts = text.split(/(?<=[.?!])\s+/);
  const total = parts.reduce((a, p) => a + p.length, 0);
  let acc = 0;
  for (const p of parts) { acc += p.length; if (lt <= (acc / total) * speak) return p; }
  return parts[parts.length - 1];
};

export const Opener = ({ firm, lt }) => lt < 3.4 ? (
  <div className="absolute inset-0 z-[65] grid place-items-center bg-[#09090B]/92 backdrop-blur-md" style={{ opacity: 1 - Math.max(0, (lt - 2.8) / 0.6) }} data-testid="demo-opener">
    <div className="text-center" style={rise(lt, 0.15)}><p className="font-mono text-[15px] uppercase tracking-[0.35em] text-[#D4AF37]">A walkthrough made for</p><p className="mt-5 font-display text-[68px] font-semibold text-zinc-50">{firm}</p></div>
  </div>
) : null;

const useClock = (record) => {
  const q = new URLSearchParams(window.location.search);
  const t0 = record ? +(q.get("t") || 0) : 0;
  const [t, setT] = useState(t0); const [playing, setPlaying] = useState(!(record && q.get("pause"))); const [sound, setSound] = useState(false);
  const audio = useRef(null); const tr = useRef(t0); const vis = useRef(true);
  useEffect(() => {
    let raf, last = performance.now(), lastSet = 0;
    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000); last = now;
      const a = audio.current;
      if (playing && (vis.current || record)) {
        tr.current = sound && a && !a.paused ? a.currentTime : tr.current + dt;
        if (tr.current >= M.total) { if (record) { window.__demoDone = true; setPlaying(false); } else { tr.current = 0; if (sound && a) { a.currentTime = 0; a.play().catch(() => {}); } } }
        if (now - lastSet > 33) { setT(tr.current); lastSet = now; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, sound, record]);
  const seek = useCallback((s) => { tr.current = s; setT(s); if (audio.current) audio.current.currentTime = s; }, []);
  const toggleSound = () => { const a = audio.current; if (!sound) { a.currentTime = tr.current; a.play().catch(() => {}); setSound(true); setPlaying(true); } else { a.pause(); setSound(false); } };
  const togglePlay = () => { const a = audio.current; if (playing) a?.pause(); else if (sound) { a.currentTime = tr.current; a.play().catch(() => {}); } setPlaying(!playing); };
  return { t, playing, sound, audio, vis, seek, toggleSound, togglePlay };
};

// §87 Reports start, each new furthest scene, voice-over on and completion (once per page view).
const useTracking = (on, sc, t, sound) => {
  const seen = useRef({ max: -1, started: false, done: false, sound: false });
  useEffect(() => {
    if (!on) return;
    const s = seen.current, i = M.scenes.findIndex((x) => x.key === sc.key);
    if (!s.started && t > 1) { s.started = true; trackDemo("start"); }
    if (s.started && i > s.max) { s.max = i; trackDemo("progress", { scene: sc.key, index: i, pct: Math.round((sc.start / M.total) * 100) }); }
    if (!s.done && t > M.total - 1.5) { s.done = true; trackDemo("complete", { pct: 100 }); }
  }, [on, sc, t]);
  useEffect(() => { if (on && sound && !seen.current.sound) { seen.current.sound = true; trackDemo("sound_on"); } }, [on, sound]);
};

const Chapters = ({ t, seek }) => (
  <div className="flex flex-1 gap-1" data-testid="demo-chapters">{M.scenes.map((s) => {
    const p = Math.max(0, Math.min(1, (t - s.start) / s.dur));
    return <button key={s.key} onClick={() => seek(s.start + 0.01)} title={SCENES[s.key][1]} className="group flex flex-col gap-1.5 py-2 text-left" style={{ flex: s.dur }} data-testid={`demo-chapter-${s.key}`}>
      <span className="h-1.5 w-full overflow-hidden rounded-full bg-white/10 transition-[height] group-hover:h-2"><span className="block h-full rounded-full bg-gradient-to-r from-[#F3C649] to-[#D4AF37]" style={{ width: `${p * 100}%` }} /></span>
      <span className={cn("hidden truncate text-[10px] lg:block", p > 0 && p < 1 ? "text-[#F3C649]" : "text-zinc-500")}>{SCENES[s.key][1]}</span></button>;
  })}</div>
);

export const useStageScale = (vis) => {
  const wrap = useRef(null); const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = wrap.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / W)); ro.observe(el);
    const io = vis && new IntersectionObserver(([e]) => { vis.current = e.isIntersecting; }, { threshold: 0.15 }); io?.observe(el);
    return () => { ro.disconnect(); io?.disconnect(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [wrap, scale];
};

export const ProductDemo = ({ record = false, firm = "", track = false }) => {
  const c = useClock(record);
  const [wrap, scale] = useStageScale(c.vis);
  const sc = [...M.scenes].reverse().find((s) => c.t >= s.start) || M.scenes[0];
  const lt = c.t - sc.start; const Scene = SCENES[sc.key][0];
  const cap = captionAt(sc.text, lt, sc.dur - 0.9);
  useTracking(track && !record, sc, c.t, c.sound);
  return (
    <FirmCtx.Provider value={firm || "Acme Recruiting"}>
    <div data-testid="product-demo">
      <div className={cn("relative", !record && "rounded-[24px] bg-gradient-to-br from-[#F3C649]/60 via-[#8224e3]/40 to-[#D4AF37]/30 p-[1.5px] shadow-[0_40px_140px_-40px_rgba(212,175,55,0.45)]")}>
        <div ref={wrap} className={cn("relative w-full overflow-hidden bg-[#09090B]", !record && "rounded-[23px]")} style={{ aspectRatio: `${W} / ${H}` }} data-testid="demo-stage">
          <div className="absolute left-0 top-0 origin-top-left" style={{ width: W, height: H, transform: `scale(${scale})` }} data-testid={`demo-scene-${sc.key}`}>
            <Scene lt={lt} />
            {firm && sc.key === "intro" && <Opener firm={firm} lt={lt} />}
            {(record || scale >= 0.6) && <div className="pointer-events-none absolute inset-x-0 bottom-6 z-[60] flex justify-center px-24"><p key={cap} className="fade-up rounded-2xl bg-black/75 px-6 py-3 text-center text-[21px] leading-snug text-zinc-50 backdrop-blur-md" data-testid="demo-caption">{cap}</p></div>}
          </div>
          {!record && !c.sound && <button onClick={c.toggleSound} className="absolute right-2 top-2 z-[70] inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F3C649] to-[#D4AF37] px-2.5 py-1.5 text-[11px] font-semibold text-black shadow-[0_0_24px_rgba(243,198,73,0.55)] transition-transform hover:scale-105 sm:right-3 sm:top-3 sm:px-3.5 sm:py-2 sm:text-sm" data-testid="demo-sound-on"><Volume2 className="h-4 w-4" /><span className="sm:hidden">Voice</span><span className="hidden sm:inline">Tap for voice-over</span></button>}
        </div>
      </div>
      {!record && scale < 0.6 && <p className="mt-3 min-h-[3.5rem] rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm leading-snug text-zinc-200" data-testid="demo-caption">{cap}</p>}
      <audio ref={c.audio} src="/demo/voiceover.mp3" preload="none" muted={record} />
      {!record && <div className="mt-4 flex items-center gap-3">
        <button onClick={c.togglePlay} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/15 text-zinc-100 transition-colors hover:border-[#D4AF37] hover:text-[#F3C649]" aria-label={c.playing ? "Pause" : "Play"} data-testid="demo-play-toggle">{c.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
        <button onClick={c.toggleSound} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/15 text-zinc-100 transition-colors hover:border-[#D4AF37]" aria-label="Voice-over" data-testid="demo-sound-toggle">{c.sound ? <Volume2 className="h-4 w-4 text-[#F3C649]" /> : <VolumeX className="h-4 w-4" />}</button>
        <Chapters t={c.t} seek={c.seek} />
        <span className="shrink-0 font-mono text-[11px] text-zinc-500" data-testid="demo-time">{fmt(c.t)} / {fmt(M.total)}</span>
        <a href={DEMO_VIDEO} target="_blank" rel="noreferrer" onClick={() => track && trackDemo("video_open")} className="hidden shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-zinc-300 transition-colors hover:border-[#c79bff] hover:text-white sm:inline-flex" data-testid="demo-video-link"><Video className="h-3.5 w-3.5" />Watch as video</a>
      </div>}
    </div>
    </FirmCtx.Provider>
  );
};

// Bare page used to record the MP4 (1280×800, no controls).
export default function DemoVideoPage() {
  return <div className="h-screen w-screen overflow-hidden bg-[#09090B]"><ProductDemo record /></div>;
}
